/**
 * Per-filter checkbox state (unset values count as enabled) and the derived MapLibre filter for `appStore.app.filters`.
 * A filter with no enabled values collapses the whole expression to `['literal', false]`.
 */

import type { ExpressionSpecification } from 'maplibre-gl';
import { appStore } from '#lib/stores/appStore.svelte.js';

type FilterStateMap = Record<string, Record<string, boolean>>;

/** Coerce a string value to number when it parses as one — keeps MapLibre
 *  comparisons against numeric properties (e.g. speed_limit) honest. */
function coerceValue(raw: string): string | number {
	if (raw === '') return raw;
	const n = Number(raw);
	return Number.isFinite(n) && String(n) === raw ? n : raw;
}

function createMapDataFilterStore() {
	// Sparse map: only filterId/value pairs the user has explicitly toggled
	// appear here. `isChecked` defaults missing entries to `true`.
	let state = $state<FilterStateMap>({});
	let showPanel = $state(false);

	function isChecked(filterId: string, value: string): boolean {
		return state[filterId]?.[value] ?? true;
	}

	const filter = $derived.by<ExpressionSpecification>(() => {
		const filters = appStore.app?.filters ?? [];
		if (filters.length === 0) return ['literal', true];

		const parts: ExpressionSpecification[] = [];
		for (const f of filters) {
			const enabled = f.options.filter((o) => isChecked(f.id, o.value));
			if (enabled.length === 0) return ['literal', false];
			parts.push([
				'any',
				...enabled.map((o): ExpressionSpecification => ['==', ['get', f.id], coerceValue(o.value)])
			]);
		}
		return parts.length === 1 ? parts[0] : ['all', ...parts];
	});

	const filteringActive = $derived.by(() => {
		const filters = appStore.app?.filters ?? [];
		return filters.some((f) => f.options.some((o) => !isChecked(f.id, o.value)));
	});

	function setValue(filterId: string, value: string, checked: boolean) {
		state = {
			...state,
			[filterId]: { ...(state[filterId] ?? {}), [value]: checked }
		};
	}

	function setAll(filterId: string, values: readonly string[], checked: boolean) {
		const next: Record<string, boolean> = { ...(state[filterId] ?? {}) };
		for (const v of values) next[v] = checked;
		state = { ...state, [filterId]: next };
	}

	function resetAll() {
		// Remove all explicit overrides — every filter falls back to its
		// default-enabled state.
		state = {};
	}

	return {
		get showPanel() {
			return showPanel;
		},
		get filter() {
			return filter;
		},
		get filteringActive() {
			return filteringActive;
		},

		isChecked,
		setValue,
		setAll,
		resetAll,

		toggleShowPanel() {
			showPanel = !showPanel;
		}
	};
}

export const mapDataFilterStore = createMapDataFilterStore();
